import React, { useState, useEffect } from 'react';
import {
  FaCheck, FaTimes, FaMoneyBillWave, FaUser, FaCalendar,
  FaEye, FaCheckCircle, FaTimesCircle, FaSpinner, FaFilter,
  FaSearch, FaDownload, FaSync, FaBuilding, FaIdCard,
  FaEnvelope, FaPhone, FaCreditCard, FaArrowLeft,
  FaArrowRight, FaExclamationTriangle, FaInfoCircle, FaUsers
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const WithdrawalRequests = () => {
  // Modal state for confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' or 'reject'
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all'); // 'all', 'reporter', 'influencer'
  const [showFilters, setShowFilters] = useState(false);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
  const paginatedRequests = filteredRequests.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const [showApproveSpinner, setShowApproveSpinner] = useState(false); // Spinner for approve/reject

  // Modal state for details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/withdrawals/requests`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        console.log('Withdrawal Requests:', response.data)
        setRequests(response.data.requests || []);
      } catch (error) {
        console.error('Error fetching withdrawal requests:', error);
        toast.error('Failed to fetch withdrawal requests');
      }
    };
    fetchRequests();
  }, []);

  // Filter requests based on search text, status, and user type
  useEffect(() => {
    let filtered = requests;

    // Filter by search text
    if (searchText) {
      filtered = filtered.filter(request =>
        request.reporterId?.iinsafId?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.reporterId?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.reporterId?.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.bankDetails?.accountHolder?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.bankDetails?.bankName?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Filter by user type (based on iinsafId prefix)
    if (userTypeFilter !== 'all') {
      filtered = filtered.filter(request => {
        const iinsafId = request.reporterId?.iinsafId || '';
        if (userTypeFilter === 'reporter') {
          return iinsafId.startsWith('REP');
        } else if (userTypeFilter === 'influencer') {
          return iinsafId.startsWith('INF');
        }
        return true;
      });
    }

    setFilteredRequests(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [requests, searchText, statusFilter, userTypeFilter]);

  const handleApprove = async (_id) => {
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/withdrawals/approve/${_id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      console.log('Withdrawal Request Approval Response:', response.data);
      if (response.data.success) {
        toast.success('Withdrawal request has been approved successfully.');
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        toast.error('Failed to approve withdrawal request.');
      }
    } catch (error) {
      toast.error('Error approving withdrawal request.');
    } finally {
      setShowApproveSpinner(false);
    }
  };

  const handleReject = async (_id) => {
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/withdrawals/${_id}/reject`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (response.data.success) {
        toast.success('Withdrawal request has been rejected successfully.');
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        toast.error('Failed to reject withdrawal request.');
      }
    } catch (error) {
      toast.error('Error rejecting withdrawal request.');
    } finally {
      setShowApproveSpinner(false);
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setUserTypeFilter('all');
  };

  const getUserType = (userData) => {
    // First try to get user type from role field
    if (userData?.role) {
      return userData.role;
    }

    // Fallback to iinsafId pattern matching
    const iinsafId = userData?.iinsafId;
    if (!iinsafId) return 'Unknown';
    if (iinsafId.startsWith('REP')) return 'Reporter';
    if (iinsafId.startsWith('INF')) return 'Influencer';
    if (iinsafId.startsWith('IINSAF')) return 'Reporter'; // Handle legacy IINSAF format
    return 'Unknown';
  };

  const getUserTypeColor = (userData) => {
    // First try to get user type from role field
    if (userData?.role) {
      switch (userData.role) {
        case 'Reporter': return 'bg-blue-100 text-blue-800';
        case 'Influencer': return 'bg-purple-100 text-purple-800';
        case 'Advertiser': return 'bg-green-100 text-green-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }

    // Fallback to iinsafId pattern matching
    const iinsafId = userData?.iinsafId;
    if (!iinsafId) return 'bg-gray-100 text-gray-800';
    if (iinsafId.startsWith('REP')) return 'bg-blue-100 text-blue-800';
    if (iinsafId.startsWith('INF')) return 'bg-purple-100 text-purple-800';
    if (iinsafId.startsWith('IINSAF')) return 'bg-blue-100 text-blue-800'; // Handle legacy IINSAF format
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <FaMoneyBillWave className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Withdrawal Requests</h1>
                  <p className="text-blue-100 mt-1">Manage user withdrawal requests and approvals</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-blue-100 text-sm">Total Requests</div>
                <div className="text-2xl font-bold text-white">{requests.length}</div>
                <div className="text-blue-100 text-sm">Filtered: {filteredRequests.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by IINSAF ID, name, email, or bank details..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center gap-2"
            >
              <FaFilter className="w-5 h-5" />
              Filters
            </button>

            {/* Refresh Button */}
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 flex items-center gap-2"
            >
              <FaSync className="w-5 h-5" />
              Refresh
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                {/* User Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
                  <select
                    value={userTypeFilter}
                    onChange={(e) => setUserTypeFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Users</option>
                    <option value="reporter">Reporters Only</option>
                    <option value="influencer">Influencers Only</option>
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <FaTimes className="w-4 h-4" />
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {filteredRequests.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                <FaMoneyBillWave className="w-12 h-12 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Withdrawal Requests</h3>
              <p className="text-gray-500 mb-6">
                {requests.length === 0
                  ? "There are no withdrawal requests to display at this time."
                  : "No requests match your current filters. Try adjusting your search criteria."
                }
              </p>
              {requests.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-indigo-600">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaIdCard className="w-4 h-4" />
                          IINSAF ID
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaUsers className="w-4 h-4" />
                          User Type
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaUser className="w-4 h-4" />
                          User Name
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaCalendar className="w-4 h-4" />
                          Date
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaMoneyBillWave className="w-4 h-4" />
                          Amount
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaCheckCircle className="w-4 h-4" />
                          Status
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaEye className="w-4 h-4" />
                          Actions
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRequests.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {request.reporterId?.iinsafId?.charAt(0) || 'U'}
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900 font-mono">
                                {request.reporterId?.iinsafId || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getUserTypeColor(request.reporterId)}`}>
                            {getUserType(request.reporterId)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {request.reporterId?.name || 'Loading...'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.reporterId?.email || 'Loading...'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(request.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-lg font-bold text-green-600">
                            ₹{request.amount?.toLocaleString() || '0'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                            {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowDetailsModal(true);
                              }}
                              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center gap-2"
                            >
                              <FaEye className="w-4 h-4" />
                              View
                            </button>
                            {request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    setShowConfirmModal(true);
                                    setConfirmAction('approve');
                                    setSelectedRequestId(request._id);
                                    setIsChecked(false);
                                  }}
                                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center gap-2"
                                >
                                  <FaCheck className="w-4 h-4" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    setShowConfirmModal(true);
                                    setConfirmAction('reject');
                                    setSelectedRequestId(request._id);
                                    setIsChecked(false);
                                  }}
                                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center gap-2"
                                >
                                  <FaTimes className="w-4 h-4" />
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden p-4 space-y-4">
                {paginatedRequests.map((request) => (
                  <div key={request._id} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                          {request.reporterId?.iinsafId?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{request.reporterId?.name || 'Loading...'}</div>
                          <div className="text-sm text-gray-600 font-mono">{request.reporterId?.iinsafId || 'Loading...'}</div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${getUserTypeColor(request.reporterId)}`}>
                            {getUserType(request.reporterId)}
                          </span>
                        </div>
                      </div>
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'Unknown'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-sm text-gray-600">Amount</div>
                        <div className="text-lg font-bold text-green-600">₹{request.amount?.toLocaleString() || '0'}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Date</div>
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailsModal(true);
                        }}
                        className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        <FaEye className="w-4 h-4" />
                        View Details
                      </button>
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => {
                              setShowConfirmModal(true);
                              setConfirmAction('approve');
                              setSelectedRequestId(request._id);
                              setIsChecked(false);
                            }}
                            className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center justify-center gap-2"
                          >
                            <FaCheck className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              setShowConfirmModal(true);
                              setConfirmAction('reject');
                              setSelectedRequestId(request._id);
                              setIsChecked(false);
                            }}
                            className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center justify-center gap-2"
                          >
                            <FaTimes className="w-4 h-4" />
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredRequests.length)} of {filteredRequests.length} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg border transition-colors duration-200 flex items-center gap-2 ${currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        <FaArrowLeft className="w-4 h-4" />
                        Previous
                      </button>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${currentPage === page
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg border transition-colors duration-200 flex items-center gap-2 ${currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        Next
                        <FaArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Loading Spinner Overlay */}
        {showApproveSpinner && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn max-w-sm mx-4">
              <div className="relative mb-4 w-16 h-16 grid place-items-center">
                <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-600 animate-spin" style={{ borderWidth: "4px" }} />
                <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-blue-500 animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
                <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-blue-400 animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
              </div>
              <span className="text-lg font-semibold text-blue-700 tracking-wide text-center">
                Processing Request...
              </span>
              <span className="text-sm text-gray-500 mt-2 text-center">
                Please wait while we update the status.
              </span>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                      <FaMoneyBillWave className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Withdrawal Request Details</h2>
                      <p className="text-blue-100 text-sm font-mono">ID: {selectedRequest._id}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center text-white hover:bg-opacity-30 transition-colors duration-200"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Request Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaInfoCircle className="w-5 h-5 text-blue-600" />
                    Request Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Amount</label>
                      <p className="text-2xl font-bold text-green-600">₹{selectedRequest.amount?.toLocaleString() || '0'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Status</label>
                      <span className={`inline-flex px-4 py-2 text-sm font-semibold rounded-full ${getStatusColor(selectedRequest.status)}`}>
                        {selectedRequest.status?.charAt(0).toUpperCase() + selectedRequest.status?.slice(1) || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Request Date</label>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(selectedRequest.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* User Information */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaUser className="w-5 h-5 text-purple-600" />
                    User Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Name</label>
                      <p className="text-lg font-semibold text-gray-900 break-words">{selectedRequest.reporterId?.name || 'Loading...'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">User Type</label>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getUserTypeColor(selectedRequest.reporterId)}`}>
                        {getUserType(selectedRequest.reporterId)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">IINSAF ID</label>
                      <p className="text-sm font-mono text-gray-900 break-all bg-gray-100 px-3 py-2 rounded-lg">{selectedRequest.reporterId?.iinsafId || 'Loading...'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Email</label>
                      <p className="text-sm text-gray-900 break-all">{selectedRequest.reporterId?.email || 'Loading...'}</p>
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaBuilding className="w-5 h-5 text-green-600" />
                    Bank Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Account Holder Name</label>
                      <p className="text-lg font-semibold text-gray-900 break-words">{selectedRequest.bankDetails?.accountHolder || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Bank Name</label>
                      <p className="text-lg font-semibold text-gray-900 break-words">{selectedRequest.bankDetails?.bankName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Account Number</label>
                      <p className="text-sm font-mono text-gray-900 break-all bg-gray-100 px-3 py-2 rounded-lg">{selectedRequest.bankDetails?.accountNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">IFSC Code</label>
                      <p className="text-sm font-mono text-gray-900 break-all bg-gray-100 px-3 py-2 rounded-lg">{selectedRequest.bankDetails?.ifscCode || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 relative">
              {/* Modal Header */}
              <div className={`px-6 py-6 rounded-t-2xl ${confirmAction === 'approve' ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-red-600 to-rose-600'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                      {confirmAction === 'approve' ? (
                        <FaCheckCircle className="w-6 h-6 text-white" />
                      ) : (
                        <FaTimesCircle className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        Confirm {confirmAction === 'approve' ? 'Approval' : 'Rejection'}
                      </h2>
                      <p className="text-white text-opacity-80 text-sm">
                        {confirmAction === 'approve' ? 'Approve this withdrawal request' : 'Reject this withdrawal request'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center text-white hover:bg-opacity-30 transition-colors duration-200"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="mb-6">
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => setIsChecked(e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      I confirm that I want to {confirmAction === 'approve' ? 'approve' : 'reject'} this withdrawal request.
                      This action cannot be undone.
                    </span>
                  </label>
                </div>

                <div className="flex space-x-3">
                  <button
                    className={`flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${confirmAction === 'approve'
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                        : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700'
                      } ${!isChecked ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl'}`}
                    disabled={!isChecked}
                    onClick={async () => {
                      if (!isChecked) return;
                      setShowConfirmModal(false);
                      if (confirmAction === 'approve') {
                        await handleApprove(selectedRequestId);
                      } else {
                        await handleReject(selectedRequestId);
                      }
                    }}
                  >
                    {confirmAction === 'approve' ? (
                      <FaCheck className="w-4 h-4" />
                    ) : (
                      <FaTimes className="w-4 h-4" />
                    )}
                    {confirmAction === 'approve' ? 'Approve Request' : 'Reject Request'}
                  </button>
                  <button
                    className="flex-1 px-6 py-3 rounded-xl font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawalRequests;